import { z } from "zod";

export const OriginSchema = z.enum([
  "user",
  "external",
  "policy",
  "research",
  "model-default",
]);
export type Origin = z.infer<typeof OriginSchema>;

export const SourcedValueSchema = z.object({
  value: z.unknown(),
  unit: z.string(),
  origin: OriginSchema,
  sourceUrl: z.string().url().optional(),
  validFrom: z.string().optional(),
  validUntil: z.string().optional(),
  retrievedAt: z.string().optional(),
});
export type SourcedValue<T> = Omit<
  z.infer<typeof SourcedValueSchema>,
  "value"
> & { value: T };

export const HouseholdSizeSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);
export type HouseholdSize = z.infer<typeof HouseholdSizeSchema>;

export type HouseholdBuildingType = "house" | "apartment";

export const HOUSEHOLD_CONSUMPTION_PRESETS_BY_BUILDING = {
  house: [
    { size: 1, label: "1 Person", annualElectricityKwh: 1_800 },
    { size: 2, label: "2 Personen", annualElectricityKwh: 2_700 },
    { size: 3, label: "3 Personen", annualElectricityKwh: 3_500 },
    { size: 4, label: "4 Personen", annualElectricityKwh: 3_800 },
    { size: 5, label: "5 Personen", annualElectricityKwh: 4_500 },
  ],
  apartment: [
    { size: 1, label: "1 Person", annualElectricityKwh: 1_200 },
    { size: 2, label: "2 Personen", annualElectricityKwh: 1_900 },
    { size: 3, label: "3 Personen", annualElectricityKwh: 2_400 },
    { size: 4, label: "4 Personen", annualElectricityKwh: 2_600 },
    { size: 5, label: "5 Personen", annualElectricityKwh: 3_100 },
  ],
} as const satisfies Record<
  HouseholdBuildingType,
  ReadonlyArray<{
    size: HouseholdSize;
    label: string;
    annualElectricityKwh: number;
  }>
>;

export const HOUSEHOLD_CONSUMPTION_PRESETS =
  HOUSEHOLD_CONSUMPTION_PRESETS_BY_BUILDING.house;

/** Current research-backed planning defaults used by the visible planner. */
export const CURRENT_PLANNING_DEFAULTS = {
  pvCostPerKwpEur: 1_000,
  pvRooftopInstallationEur: 5_000,
  batteryCostPerUsableKwhEur: 650,
  balconyPvCapacityKwp: 0.8,
} as const;

export const DEFAULT_ELECTRICITY_PRICE_EUR_PER_KWH = 0.33;

/**
 * Simplified normalized shape of the German H0 household load profile.
 * The model normalizes these weights before using them for hourly simulation.
 */
export const HOUSEHOLD_H0_LOAD_PROFILE = [
  0.018, 0.017, 0.016, 0.016, 0.017, 0.021, 0.03, 0.039, 0.043, 0.038, 0.034,
  0.032, 0.031, 0.031, 0.032, 0.035, 0.041, 0.056, 0.078, 0.096, 0.103, 0.088,
  0.064, 0.043,
] as const;

export const HOUSEHOLD_CONSUMPTION_SOURCE_URL =
  "https://www.co2online.de/energie-sparen/strom-sparen/strom-sparen-stromspartipps/stromverbrauch-im-haushalt/";
export const HOUSEHOLD_LOAD_PROFILE_SOURCE_URL =
  "https://www.bdew.de/media/documents/2000131_Anwendung-repraesentativen_Lastprofile-Step-by-step.pdf";

const RoofSchema = z
  .object({
    tiltDeg: z.number().min(0).max(90),
    azimuthDeg: z.number().min(-180).max(180),
    shading: z.enum(["none", "low", "medium", "high"]),
    usableAreaM2: z.number().positive().max(1_000).optional(),
  })
  .strict();

const AssetIdSchema = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
  .max(64);

export const AssetStateSchema = z.enum(["new", "existing"]);
export type AssetState = z.infer<typeof AssetStateSchema>;

export const SolarVariantSchema = z.enum(["rooftop", "balcony"]);
export type SolarVariant = z.infer<typeof SolarVariantSchema>;

export const AssetCostOverrideSchema = z
  .object({
    totalEur: z.number().nonnegative().optional(),
    equipmentEur: z.number().nonnegative().optional(),
    installationEur: z.number().nonnegative().optional(),
    otherOneTimeEur: z.number().nonnegative().optional(),
  })
  .strict();
export type AssetCostOverride = z.infer<typeof AssetCostOverrideSchema>;

const SolarAssetBaseSchema = z
  .object({
    id: AssetIdSchema,
    kind: z.literal("solar"),
    state: AssetStateSchema,
    capacityKwp: z.number().positive().max(100),
    areaM2: z.number().positive().max(10_000).optional(),
    areaM2PerKwp: z.number().positive().max(100).optional(),
    yieldKwhPerKwp: z.number().positive().max(5_000).optional(),
    costPerKwpEur: z.number().nonnegative().max(100_000).optional(),
    costOverride: AssetCostOverrideSchema.optional(),
  })
  .strict();

export const RooftopSolarAssetSchema = SolarAssetBaseSchema.extend({
  variant: z.literal("rooftop"),
}).strict();

export const BalconySolarAssetSchema = SolarAssetBaseSchema.extend({
  variant: z.literal("balcony"),
  inverterCapacityKva: z.number().positive().max(10).optional(),
}).strict();

export const SolarAssetSchema = z.union([
  RooftopSolarAssetSchema,
  BalconySolarAssetSchema,
]);
export type SolarAsset = z.infer<typeof SolarAssetSchema>;

export const BatteryAssetSchema = z
  .object({
    id: AssetIdSchema,
    kind: z.literal("battery"),
    state: AssetStateSchema,
    usableCapacityKwh: z.number().positive().max(1_000),
    nominalCapacityKwh: z.number().positive().max(1_000).optional(),
    costPerKwhEur: z.number().nonnegative().max(100_000).optional(),
    costOverride: AssetCostOverrideSchema.optional(),
    replacement: z
      .object({
        year: z.number().int().min(2).max(40),
        costFraction: z.number().min(0).max(2),
      })
      .strict()
      .optional(),
  })
  .strict();
export type BatteryAsset = z.infer<typeof BatteryAssetSchema>;

export const EnergyAssetSchema = z.union([
  SolarAssetSchema,
  BatteryAssetSchema,
]);
export type EnergyAsset = z.infer<typeof EnergyAssetSchema>;

export const PlanDraftV2Schema = z
  .object({
    commissioningDate: z.string().date(),
    location: z
      .object({
        postalCode: z.string().regex(/^\d{5}$/),
        coordinates: z
          .object({ lat: z.number(), lon: z.number() })
          .strict()
          .optional(),
      })
      .strict(),
    household: z
      .object({
        annualElectricityKwh: z.number().positive().max(100_000),
        electricityPriceEurPerKwh: z.number().nonnegative().max(10).optional(),
        householdSize: HouseholdSizeSchema.optional(),
      })
      .strict(),
    roof: RoofSchema,
    assets: z
      .array(EnergyAssetSchema)
      .max(100)
      .superRefine((assets, context) => {
        const seen = new Set<string>();
        assets.forEach((asset, index) => {
          if (seen.has(asset.id))
            context.addIssue({
              code: z.ZodIssueCode.custom,
              path: [index, "id"],
              message: "Asset-IDs müssen innerhalb eines Plans eindeutig sein.",
            });
          seen.add(asset.id);
        });
      }),
    assumptionOverrides: z
      .object({
        electricityPriceEurPerKwh: z.number().nonnegative().optional(),
        feedInTariffEurPerKwh: z.number().nonnegative().optional(),
        electricityPriceRealGrowthRate: z.number().min(-1).max(1).optional(),
        realDiscountRate: z.number().min(0).max(1).optional(),
        analysisYears: z
          .union([z.literal(20), z.literal(25), z.literal(30)])
          .optional(),
      })
      .optional(),
  })
  .strict();
export type PlanDraftV2 = z.infer<typeof PlanDraftV2Schema>;
export const PlanDraftSchema = PlanDraftV2Schema;

export const LegacyPlanDraftV1Schema = z.object({
  commissioningDate: z.string().date(),
  location: z
    .object({
      postalCode: z.string().regex(/^\d{5}$/),
      coordinates: z
        .object({ lat: z.number(), lon: z.number() })
        .strict()
        .optional(),
    })
    .strict(),
  household: z
    .object({
      annualElectricityKwh: z.number().positive().max(100_000),
      electricityPriceEurPerKwh: z.number().nonnegative().max(10).optional(),
      householdSize: HouseholdSizeSchema.optional(),
    })
    .strict(),
  roof: RoofSchema,
  components: z
    .object({
      solar: z
        .object({
          capacityKwp: z.number().min(2).max(20),
          acquisition: z.enum(["buy", "owned"]).optional(),
          installedCostEur: z.number().nonnegative().optional(),
        })
        .optional(),
      battery: z
        .object({
          usableCapacityKwh: z.number().positive().max(50),
          acquisition: z.enum(["buy", "owned"]).optional(),
          installedCostEur: z.number().nonnegative().optional(),
          replacementYear: z.number().int().min(2).max(25).optional(),
          replacementCostFraction: z.number().min(0).max(2).optional(),
        })
        .optional(),
    })
    .strict(),
  assumptionOverrides: z
    .object({
      electricityPriceEurPerKwh: z.number().nonnegative().optional(),
      feedInTariffEurPerKwh: z.number().nonnegative().optional(),
      electricityPriceRealGrowthRate: z.number().min(-1).max(1).optional(),
      realDiscountRate: z.number().min(0).max(1).optional(),
      analysisYears: z
        .union([z.literal(20), z.literal(25), z.literal(30)])
        .optional(),
    })
    .optional(),
});
export type LegacyPlanDraftV1 = z.infer<typeof LegacyPlanDraftV1Schema>;

export type AssumptionValue = SourcedValue<number | string>;

export const AssumptionSetSchema = z.object({
  version: z.string(),
  effectiveFrom: z.string(),
  effectiveUntil: z.string().optional(),
  values: z.record(z.string(), SourcedValueSchema),
  sizingRules: z.object({
    pvMinKwp: z.number(),
    pvMaxKwp: z.number(),
    pvPresetSmallFactor: z.number(),
    pvPresetLargeFactor: z.number(),
    batteryUtilizationMin: z.number(),
    batteryUtilizationMax: z.number(),
  }),
});
export type AssumptionSetV1 = z.infer<typeof AssumptionSetSchema>;

export type ExternalSourceMetadata = {
  provider: string;
  url?: string;
  retrievedAt?: string;
  status: "live" | "fallback" | "provided";
  confidence: "high" | "medium" | "low";
  note?: string;
};

export type AnnualFlow = {
  year: number;
  generationKwh: number;
  directUseKwh: number;
  batteryChargeKwh: number;
  batteryDischargeKwh: number;
  batteryLossKwh: number;
  exportKwh: number;
  gridImportKwh: number;
  totalLoadKwh: number;
  electricityCostEur: number;
  feedInRevenueEur: number;
  /**
   * Value that exported solar electricity could have created by displacing
   * grid purchases, after accounting for the feed-in revenue received.
   */
  missedSelfConsumptionValueEur: number;
  operatingCostEur: number;
  replacementCostEur: number;
  baselineCostEur: number | null;
  planCostEur: number | null;
  cashBenefitEur: number | null;
  discountedCashBenefitEur: number | null;
};

export type CostBreakdown = {
  equipmentEur: number;
  installationEur: number;
  otherOneTimeEur: number;
  totalEur: number;
};

export type ScenarioCostBreakdown = {
  byAssetId: Record<string, CostBreakdown>;
  totalsByKind: {
    solar: CostBreakdown;
    battery: CostBreakdown;
  };
  totals: CostBreakdown;
};

export type ScenarioMetrics = {
  upfrontCostEur: number;
  upfrontCostBreakdown: ScenarioCostBreakdown;
  firstYearCashBenefitEur: number | null;
  simplePaybackYears: number | null;
  sustainedPaybackYears: number | null;
  npvEur: number | null;
  undiscountedCashPositionEur: number | null;
  selfConsumptionPct: number;
  selfSufficiencyPct: number;
};

export type Scenario = {
  id: "baseline" | "selected" | "pvOnly" | "withoutBattery";
  label: string;
  assetIds: string[];
  metrics: ScenarioMetrics;
  annualFlows: AnnualFlow[];
  unavailableReason?: string;
  warnings: string[];
};

export type ComponentVerdict = {
  component: "battery";
  comparatorScenario: string;
  npvDeltaEur: number | null;
  paybackYears: number | null;
  verdict: "helps" | "hurts" | "neutral" | "unavailable";
  explanation: string;
};

export type PlanEvaluationV2 = {
  schemaVersion: "2";
  modelVersion: string;
  assumptionSetVersion: string;
  evaluationFingerprint: string;
  resolvedInput: PlanDraftV2;
  suggestions: {
    pv: {
      smallKwp: number;
      recommendedKwp: number;
      largeKwp: number;
      annualYieldKwhPerKwp: number;
      standardCostPerKwpEur: number;
      standardRooftopInstallationEur: number;
      source: SourcedValue<number>;
    };
    battery: {
      recommendedKwh: number;
      standardCostPerUsableKwhEur: number;
      source: SourcedValue<number>;
    };
  };
  externalSources: ExternalSourceMetadata[];
  baseline: Scenario;
  selected: Scenario;
  pvOnly: Scenario;
  withoutBattery?: Scenario;
  verdicts: ComponentVerdict[];
  warnings: string[];
  formulaTrace: string[];
};

export const PlanEvaluationSchema = z.object({
  schemaVersion: z.literal("2"),
  modelVersion: z.string(),
  assumptionSetVersion: z.string(),
  evaluationFingerprint: z.string(),
  resolvedInput: PlanDraftV2Schema,
  suggestions: z.object({
    pv: z.object({
      smallKwp: z.number(),
      recommendedKwp: z.number(),
      largeKwp: z.number(),
      annualYieldKwhPerKwp: z.number(),
      standardCostPerKwpEur: z.number(),
      standardRooftopInstallationEur: z.number(),
      source: SourcedValueSchema,
    }),
    battery: z.object({
      recommendedKwh: z.number(),
      standardCostPerUsableKwhEur: z.number(),
      source: SourcedValueSchema,
    }),
  }),
  externalSources: z.array(
    z.object({
      provider: z.string(),
      url: z.string().optional(),
      retrievedAt: z.string().optional(),
      status: z.enum(["live", "fallback", "provided"]),
      confidence: z.enum(["high", "medium", "low"]),
      note: z.string().optional(),
    }),
  ),
  baseline: z.unknown(),
  selected: z.unknown(),
  pvOnly: z.unknown(),
  withoutBattery: z.unknown().optional(),
  verdicts: z.array(z.unknown()),
  warnings: z.array(z.string()),
  formulaTrace: z.array(z.string()),
});

export type ApiError = { code: string; message: string; issues?: unknown[] };

export const ContentEventNameSchema = z.enum([
  "content_view",
  "article_engaged",
  "planner_cta_click",
  "planner_start",
  "evaluation_complete",
]);

export const ContentEventSchema = z
  .object({
    eventName: ContentEventNameSchema,
    contentId: z
      .string()
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
      .optional(),
    referrerCategory: z
      .enum(["organic", "social", "partner", "direct", "other"])
      .optional(),
    utmSource: z
      .string()
      .regex(/^[a-z0-9_-]{1,64}$/)
      .optional(),
    utmMedium: z
      .string()
      .regex(/^[a-z0-9_-]{1,64}$/)
      .optional(),
    utmCampaign: z
      .string()
      .regex(/^[a-z0-9_-]{1,96}$/)
      .optional(),
    deviceClass: z.enum(["mobile", "tablet", "desktop", "unknown"]).optional(),
  })
  .strict();

export type ContentEvent = z.infer<typeof ContentEventSchema>;
